import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { ApiError, type ApiResponse } from '@/types/api';

/**
 * Shared axios instance for the redemption flow. Same envelope contract
 * as the learner app — `/request/*` endpoints return an ApiResponse and
 * we throw ApiError on `success: false`.
 *
 * Cookies are sent (PHPSESSID) so that addVoucherCode → login/create →
 * processTransaction all run inside the same backend session.
 */

function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: '/',
    withCredentials: true,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json',
    },
  });

  client.interceptors.response.use(
    (response) => {
      const body = response.data as ApiResponse<unknown>;
      if (!body || typeof body !== 'object' || !('success' in body)) {
        return response;
      }
      if (body.success === false) {
        throw new ApiError(body.msg || 'API request failed', body);
      }
      response.data = body.data;
      return response;
    },
  );

  return client;
}

export const api = createClient();

/**
 * Legacy `/request/<api>/<action>` endpoints expect form-encoded fields.
 * Same shape the learner uses for callApi.
 */
export async function callApi<TData = unknown>(
  apiName: string,
  action: string,
  params: Record<string, unknown> = {},
  config?: AxiosRequestConfig,
): Promise<TData> {
  const url = `/request/${apiName}/${action}`;
  const form = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    form.append(key, typeof value === 'string' ? value : JSON.stringify(value));
  }
  const response = await api.post<TData>(url, form, {
    ...config,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...(config?.headers || {}),
    },
  });
  return response.data as TData;
}
